// Create or edit a metric definition (`POST /metrics`, `PATCH /metrics/{id}`; coverage rows 40,
// 43). A new metric can start from the seeded template catalog (`GET /metrics/templates`, row
// 41): picking a template metric fills every field, its event query included. KPI-ASSIGN: the
// source "From events" opens the event-metric builder ("Count / sum of events of type … where
// …", features/kpi/event-query.tsx), and a formula is written with metric pickers instead of
// typed keys. The key is fixed once created. Agency-only visibility is offered to agency
// operators only (the API refuses it otherwise).
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef, MetricTemplate } from "../../api/types";
import { EventQueryBuilder, formOfQuery, type QueryForm, queryOfForm } from "../kpi/event-query";
import {
  formOfTaskQuery,
  TaskQueryBuilder,
  type TaskQueryForm,
  taskAggregation,
  taskQueryOfForm,
} from "../kpi/task-query";
import { keys, metricTemplatesQuery } from "../queries";
import {
  AGGREGATION_OPTIONS,
  CADENCE_OPTIONS,
  DIRECTION_OPTIONS,
  GAP_OPTIONS,
  SOURCE_OPTIONS,
  UNIT_OPTIONS,
} from "./labels";
import { FormError } from "./parts";

const KEY = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;

type Form = {
  key: string;
  name: string;
  description: string;
  unit: MetricDef["unit"];
  direction: MetricDef["direction"];
  aggregation: MetricDef["aggregation"];
  cadence: NonNullable<MetricDef["cadence"]> | "";
  source: MetricDef["source"];
  query: QueryForm;
  /** T3-TASK-ROLLUPS: what a `task` metric counts on its board. */
  task: TaskQueryForm;
  formula: string;
  visibility: MetricDef["visibility"];
  gap_policy: MetricDef["gap_policy"];
};

const blank: Form = {
  key: "",
  name: "",
  description: "",
  unit: "number",
  direction: "gte",
  aggregation: "cumulative",
  cadence: "weekly",
  source: "manual",
  query: formOfQuery(null),
  task: formOfTaskQuery(null),
  formula: "",
  visibility: "client",
  gap_policy: "excuse",
};

const formOf = (m: MetricDef): Form => ({
  key: m.key,
  name: m.name,
  description: m.description ?? "",
  unit: m.unit,
  direction: m.direction,
  aggregation: m.aggregation,
  cadence: m.cadence ?? "",
  source: m.source,
  query: formOfQuery(m.event_query),
  task: formOfTaskQuery(m.task_query),
  formula: m.formula ?? "",
  visibility: m.visibility,
  gap_policy: m.gap_policy,
});

const fromTemplate = (t: MetricTemplate): Form => ({
  ...blank,
  key: t.key,
  name: t.name,
  unit: t.unit,
  direction: t.direction,
  aggregation: t.aggregation,
  cadence: t.cadence ?? "",
  source: t.source,
  query: formOfQuery(t.event_query),
  // A task template gives the shape; the board and the number are picked here.
  task: t.task_shape
    ? {
        ...formOfTaskQuery(null),
        measure: t.task_shape.measure,
        date: t.task_shape.date,
        only: t.task_shape.filter_done ? "done" : "any",
      }
    : formOfTaskQuery(null),
  formula: t.formula ?? "",
  visibility: t.visibility,
});

export function MetricDialog({
  api,
  co,
  metric,
  existingKeys,
  agency,
  onClose,
  onSaved,
  metrics = [],
}: {
  api: Api;
  co: string;
  /** The metric to edit; absent = a new one. */
  metric?: MetricDef;
  /** Keys already defined (a template already used is not offered again). */
  existingKeys: ReadonlySet<string>;
  /** The caller is an agency operator (may define agency-only metrics). */
  agency: boolean;
  onClose: () => void;
  /** Called with the saved definition (the KPI assignment editor picks a new metric this way). */
  onSaved?: (m: MetricDef) => void;
  /** The company's metrics, offered as pickers in a formula. */
  metrics?: readonly MetricDef[];
}) {
  const qc = useQueryClient();
  const editing = metric !== undefined;
  const [form, setForm] = useState<Form>(() => (metric ? formOf(metric) : blank));
  const [template, setTemplate] = useState("");
  const templates = useQuery({ ...metricTemplatesQuery(api, co), enabled: !editing });
  const choices = (templates.data?.data ?? []).flatMap((set) =>
    set.metrics
      .filter((m) => !existingKeys.has(m.key))
      .map((m) => ({
        id: `${set.template_key}:${m.key}`,
        label: `${m.name} (${set.template_key})`,
        m,
      })),
  );
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const built = queryOfForm(form.query);
  const builtTask = taskQueryOfForm(form.task);
  const isTask = form.source === "task";
  const body = {
    name: form.name.trim(),
    description: form.description.trim() || null,
    unit: form.unit,
    direction: form.direction,
    // A task metric's aggregation follows its measure (an average averages).
    aggregation: isTask ? taskAggregation(form.task.measure) : form.aggregation,
    cadence: form.cadence || null,
    ...(form.source === "event"
      ? {
          source: "event" as const,
          formula: null,
          event_query: "query" in built ? built.query : null,
          ...(editing ? { task_query: null } : {}),
        }
      : {
          source: form.source,
          formula: form.source === "formula" ? form.formula.trim() : null,
          ...(editing ? { event_query: null } : {}),
          ...(isTask
            ? { task_query: "query" in builtTask ? builtTask.query : null }
            : editing
              ? { task_query: null }
              : {}),
        }),
    visibility: form.visibility,
    gap_policy: form.gap_policy,
  };
  const save = useMutation({
    mutationFn: () =>
      metric
        ? api.client.metrics.update({ params: { co, id: metric.id }, body })
        : api.client.metrics.create({ params: { co }, body: { ...body, key: form.key } as never }),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: keys.metrics(co) });
      onSaved?.(saved);
      onClose();
    },
  });
  const keyOk = editing || (KEY.test(form.key) && form.key.length <= 64);
  const keyTaken = !editing && existingKeys.has(form.key);
  const formulaOk = form.source !== "formula" || form.formula.trim() !== "";
  // An event metric needs a valid query and a cadence (the period its events are counted in).
  const eventOk = form.source !== "event" || ("query" in built && form.cadence !== "");
  // A task metric needs a complete query and a cadence (the period its tasks are counted in).
  const taskOk = !isTask || ("query" in builtTask && form.cadence !== "");
  const ready = keyOk && !keyTaken && form.name.trim() !== "" && formulaOk && eventOk && taskOk;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[640px]">
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-5"
          aria-labelledby="metric-dialog-title"
        >
          <DialogHeader>
            <DialogTitle id="metric-dialog-title">
              {editing ? `Edit ${metric.name}` : "New metric"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "The key stays as it is: formulas, templates and imports refer to it."
                : "A metric is defined once for the company, then added to seats with a weight."}
            </DialogDescription>
          </DialogHeader>
          {!editing ? (
            <Field>
              <FieldLabel htmlFor="metric-template">Start from a template</FieldLabel>
              <NativeSelect
                id="metric-template"
                value={template}
                disabled={!choices.length}
                onChange={(e) => {
                  setTemplate(e.target.value);
                  const pick = choices.find((c) => c.id === e.target.value);
                  setForm(pick ? fromTemplate(pick.m) : blank);
                }}
              >
                <NativeSelectOption value="">
                  {templates.isPending
                    ? "Loading templates…"
                    : choices.length
                      ? "Blank metric"
                      : "No unused template metric"}
                </NativeSelectOption>
                {choices.map((c) => (
                  <NativeSelectOption key={c.id} value={c.id}>
                    {c.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <FieldDescription>
                The seeded DE catalog: name, unit, source and direction.
              </FieldDescription>
            </Field>
          ) : null}
          <div className="grid gap-5 sm:grid-cols-2">
            <Field data-invalid={!keyOk || keyTaken ? true : undefined}>
              <FieldLabel htmlFor="metric-key">Key</FieldLabel>
              <Input
                id="metric-key"
                value={form.key}
                disabled={editing}
                required
                placeholder="de.outcomes"
                aria-invalid={form.key && (!keyOk || keyTaken) ? true : undefined}
                onChange={(e) => set("key", e.target.value.trim())}
              />
              <FieldDescription>
                {keyTaken
                  ? "A metric with this key exists."
                  : "Dotted lower case, e.g. sales.calls_made."}
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="metric-name">Name</FieldLabel>
              <Input
                id="metric-name"
                value={form.name}
                required
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="metric-description">Description</FieldLabel>
            <Textarea
              id="metric-description"
              rows={2}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-3">
            <Select
              id="metric-unit"
              label="Unit"
              value={form.unit}
              options={UNIT_OPTIONS}
              onChange={(v) => set("unit", v)}
            />
            <Select
              id="metric-direction"
              label="Good when"
              value={form.direction}
              options={DIRECTION_OPTIONS}
              onChange={(v) => set("direction", v)}
            />
            <Select
              id="metric-cadence"
              label="Cadence"
              value={form.cadence}
              options={[{ value: "" as const, label: "None" }, ...CADENCE_OPTIONS]}
              onChange={(v) => set("cadence", v)}
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Select
              id="metric-aggregation"
              label="Over a period"
              value={isTask ? taskAggregation(form.task.measure) : form.aggregation}
              options={AGGREGATION_OPTIONS}
              disabled={isTask}
              onChange={(v) => set("aggregation", v)}
            />
            <Select
              id="metric-gap"
              label="Missing values"
              value={form.gap_policy}
              options={GAP_OPTIONS}
              onChange={(v) => set("gap_policy", v)}
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Select
              id="metric-source"
              label="Source"
              value={form.source}
              options={SOURCE_OPTIONS}
              onChange={(v) => set("source", v)}
            />
            <Select
              id="metric-visibility"
              label="Visible to"
              value={form.visibility}
              options={
                agency || form.visibility === "agency"
                  ? [
                      { value: "client" as const, label: "Client and agency" },
                      { value: "agency" as const, label: "Agency only" },
                    ]
                  : [{ value: "client" as const, label: "Client and agency" }]
              }
              disabled={!agency}
              onChange={(v) => set("visibility", v)}
            />
          </div>
          {form.source === "event" ? (
            <>
              <EventQueryBuilder value={form.query} onChange={(q) => set("query", q)} />
              {form.cadence === "" ? (
                <p className="text-xs text-danger-text" role="alert">
                  An event metric needs a cadence: the period its events are counted in.
                </p>
              ) : null}
            </>
          ) : null}
          {isTask ? (
            <>
              <TaskQueryBuilder
                api={api}
                co={co}
                value={form.task}
                onChange={(t) => set("task", t)}
              />
              {form.cadence === "" ? (
                <p className="text-xs text-danger-text" role="alert">
                  A task metric needs a cadence: the period its tasks are counted in.
                </p>
              ) : null}
            </>
          ) : null}
          {form.source === "formula" ? (
            <FormulaField
              value={form.formula}
              ok={formulaOk}
              metrics={metrics.filter((m) => m.key !== form.key && !m.archived_at)}
              onChange={(v) => set("formula", v)}
            />
          ) : null}
          <FormError
            what={editing ? "Saving the metric" : "Creating the metric"}
            error={save.error}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="lg" disabled={!ready || save.isPending}>
              {save.isPending ? "Saving…" : editing ? "Save metric" : "Create metric"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A formula with metric pickers: clicking a metric inserts its key, never typed by hand. */
function FormulaField({
  value,
  ok,
  metrics,
  onChange,
}: {
  value: string;
  ok: boolean;
  metrics: readonly MetricDef[];
  onChange: (v: string) => void;
}) {
  const [find, setFind] = useState("");
  const q = find.trim().toLowerCase();
  const shown = metrics
    .filter((m) => !q || m.name.toLowerCase().includes(q) || m.key.includes(q))
    .slice(0, 12);
  const insert = (s: string) => onChange(value.trim() ? `${value.trimEnd()} ${s}` : s);
  return (
    <Field data-invalid={ok ? undefined : true}>
      <FieldLabel htmlFor="metric-formula">Formula</FieldLabel>
      <Input
        id="metric-formula"
        value={value}
        required
        placeholder="Pick metrics below, with + − × ÷ between them"
        className="font-mono"
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-1" data-slot="formula-pickers">
        {["+", "-", "*", "/", "(", ")"].map((op) => (
          <Button
            key={op}
            type="button"
            size="xs"
            variant="outline"
            className="w-7 font-mono"
            aria-label={`Insert ${op}`}
            onClick={() => insert(op)}
          >
            {op}
          </Button>
        ))}
        <Input
          aria-label="Find a metric for the formula"
          placeholder="Find a metric…"
          className="ml-1 h-7 w-40"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />
      </div>
      {shown.length ? (
        <div className="flex flex-wrap gap-1">
          {shown.map((m) => (
            <Button
              key={m.id}
              type="button"
              size="xs"
              variant="secondary"
              title={m.key}
              aria-label={`Insert ${m.name} (${m.key})`}
              onClick={() => insert(m.key)}
            >
              + {m.name}
            </Button>
          ))}
        </div>
      ) : null}
      <FieldDescription>
        Other metrics by key, with + − × ÷ and parentheses; dividing by zero gives no value.
      </FieldDescription>
    </Field>
  );
}

function Select<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <NativeSelect
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <NativeSelectOption key={o.value} value={o.value}>
            {o.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );
}
